import React from 'react';

import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import CodeIcon from '@mui/icons-material/Code';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import NotesIcon from '@mui/icons-material/Notes';
import SchemaIcon from '@mui/icons-material/Schema';
import TableChartIcon from '@mui/icons-material/TableChart';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { EditorViewMode, ViewModeToggle } from 'components/editor/ViewModeToggle';

import { getGroupedComponents } from './embeds/registry';
import { KnzCodeEditor } from './monaco/types';
import { ComponentDef } from './types';

/** グループキー → 英語ラベル・アイコンのマッピング（ツールバー用。色は付けない） */
const GROUP_META: Record<string, { label: string; icon: React.ReactNode }> = {
  structure: { label: 'Structure', icon: <AccountTreeIcon sx={{ fontSize: 15 }} /> },
  text: { label: 'Text', icon: <NotesIcon sx={{ fontSize: 15 }} /> },
  data: { label: 'Data', icon: <TableChartIcon sx={{ fontSize: 15 }} /> },
  layout: { label: 'Layout', icon: <ViewModuleIcon sx={{ fontSize: 15 }} /> },
  media: { label: 'Media', icon: <SchemaIcon sx={{ fontSize: 15 }} /> },
  prose: { label: 'Prose', icon: <AutoStoriesIcon sx={{ fontSize: 15 }} /> },
};

/** リファレンス画面のパス（別タブで開く） */
export const KNZ_REFERENCE_PATH = '/workspace/knz-reference';

type Props = {
  /** Monaco editor インスタンスの取得関数 */
  getEditor: () => KnzCodeEditor | null;
  /** ツールバーを非表示にする */
  onHide?: () => void;
  /** 表示モード */
  viewMode: EditorViewMode;
  /** 表示モードを変更する */
  onViewModeChange: (mode: EditorViewMode) => void;
};

/**
 * グループごとの挿入ボタン。クリックで子ポップアップ（Menu）を開き、
 * そのグループのコンポーネントを一覧表示する（Excel のリボン風）。
 * グループ色のアイコン付きでスタイリッシュに見せる。
 */
const GroupInsertButton: React.FC<{
  groupKey: string;
  defs: ComponentDef[];
  onInsert: (def: ComponentDef) => void;
}> = ({ groupKey, defs, onInsert }) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const meta = GROUP_META[groupKey] ?? { label: groupKey, icon: null };

  return (
    <>
      <ButtonBase
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.375,
          px: 0.75,
          py: 0.375,
          borderRadius: 1.5,
          color: open ? 'text.primary' : 'text.secondary',
          bgcolor: open ? 'action.selected' : 'transparent',
          transition: 'background-color 0.15s, color 0.15s',
          '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
        }}
      >
        <Box sx={{ display: 'flex' }}>{meta.icon}</Box>
        <Typography variant="caption" fontWeight={600} sx={{ lineHeight: 1 }}>
          {meta.label}
        </Typography>
        <ExpandMoreIcon
          sx={{
            fontSize: 14,
            transition: 'transform 0.2s',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </ButtonBase>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: { border: 1, borderColor: 'divider', borderRadius: 2, minWidth: 220, mt: 0.5 },
          },
        }}
      >
        {defs.map((def) => (
          <MenuItem
            key={def.name}
            onClick={() => {
              onInsert(def);
              setAnchorEl(null);
            }}
            sx={{ py: 0.5 }}
          >
            <ListItemText
              primary={def.name}
              secondary={def.label}
              primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

/**
 * knz エディタ上部ツールバー。
 * - 装飾（太字・斜体・インラインコード）
 * - グループごとの挿入ボタン（子ポップアップでコンポーネント選択）
 * - ヒントボタン（リファレンス画面を別タブで開く）
 */
export const KnzToolbar: React.FC<Props> = ({ getEditor, onHide, viewMode, onViewModeChange }) => {
  const groups = React.useMemo(() => getGroupedComponents(), []);

  /** 選択範囲を before/after で囲む */
  const wrapSelection = (before: string, after: string) => {
    const ed = getEditor();
    if (!ed) return;
    const sel = ed.getSelection();
    if (!sel) return;
    const model = ed.getModel();
    if (!model) return;
    const text = model.getValueInRange(sel);
    ed.executeEdits('knz-toolbar', [
      { range: sel, text: `${before}${text}${after}`, forceMoveMarkers: true },
    ]);
    ed.focus();
  };

  /** カーソル位置にテンプレートを挿入 */
  const insertText = (text: string) => {
    const ed = getEditor();
    if (!ed) return;
    const sel = ed.getSelection();
    if (!sel) return;
    ed.executeEdits('knz-toolbar', [{ range: sel, text: `\n${text}\n`, forceMoveMarkers: true }]);
    ed.focus();
  };

  const openReference = () => {
    window.open(KNZ_REFERENCE_PATH, '_blank', 'noopener,noreferrer');
  };

  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap' }}
    >
      <Tooltip title="太字">
        <IconButton size="small" onClick={() => wrapSelection('**', '**')}>
          <FormatBoldIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="斜体">
        <IconButton size="small" onClick={() => wrapSelection('*', '*')}>
          <FormatItalicIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="インラインコード">
        <IconButton size="small" onClick={() => wrapSelection('`', '`')}>
          <CodeIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* グループごとの挿入ボタン（子ポップアップ） */}
      {groups.map((g) => (
        <GroupInsertButton
          key={g.key}
          groupKey={g.key}
          defs={g.defs}
          onInsert={(def) => insertText(def.template)}
        />
      ))}

      <Box sx={{ flex: 1 }} />

      <ViewModeToggle value={viewMode} onChange={onViewModeChange} />
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      <Tooltip title="knz の書き方（別タブ）">
        <IconButton size="small" onClick={openReference}>
          <HelpOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {onHide && (
        <Tooltip title="ツールバーを隠す">
          <IconButton size="small" onClick={onHide}>
            <KeyboardArrowUpIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};

export default KnzToolbar;
